/**
 * The family, as this device can read it.
 *
 * Everything the API returns about a person or a reason is ciphertext. This
 * module is the single place that turns it back into text, so every screen
 * above it works with names and reasons and never touches a key.
 *
 * A person's `pf` is an encrypted JSON fragment. Today it holds a name; the
 * avatar parts join it later, which is why it is a fragment rather than a
 * `nameCt` field. One shape now means no migration then.
 */

import type { ConsentStatus } from './consent';
import { api } from '../api/client';
import type { AvatarSpec } from '../avatar/avatar';
import { DEFAULT_UNIT, setUnit, type Unit } from './money';
import { setFamilyScene } from '../valley/scene';
import { parseFragment, sealable, type PersonFragment } from '../avatar/fragment';

export { parseFragment, type PersonFragment };
import { decryptField, encryptField } from '../crypto/familyKey';
import { forgetKey } from '../crypto/vault';
import {
  FAMILY,
  createPersonKey,
  openPersonKey,
  openReason,
  sealPersonKey,
  sealReason,
} from '../crypto/content';

export interface Person {
  id: string;
  slot: number;
  status: 'pending' | 'approved' | 'removed';
  /** Decrypted here. Empty when this device could not open the fragment. */
  name: string;
  /**
   * This person's own key, opened from `pfw` with the family key.
   *
   * Null for somebody added before the scheme existed -- their fragment is
   * sealed directly under the family key and their rows have no per-person
   * wrapping. They keep working; they just cannot be handed to a child device
   * until they are next saved.
   */
  key: CryptoKey | null;
  /**
   * This person's own code, still sealed under the family key. Opened by
   * `readChildCode` rather than here, because most screens have no business
   * with it and a decrypted code sitting in the roster is a decrypted code
   * on every screen that renders one.
   */
  codeCt: string | null;
  /** Integer cents. Derived by the server from the ledger, never stored. */
  held: number;
  shared: number;
  /**
   * Their character. Null for somebody who has never had one drawn -- the
   * screens draw a default face rather than nothing.
   *
   * Inside the same sealed fragment as the name, because a character is a
   * description of how a child looks, down to their skin tone, and the
   * server has no more business reading that than reading their name.
   */
  avatar: AvatarSpec | null;
  /**
   * A look the child has designed and sent for a grown-up to approve. Opened
   * here with their key; null when nothing is waiting.
   *
   * It is a whole fragment -- name included -- because that is what the
   * server stores and what approving publishes. `renamed` is set when the
   * name inside it differs, so a parent is shown that before saying yes.
   */
  pending: { avatar: AvatarSpec | null; name: string; renamed: boolean } | null;
  /** An earlier character is stashed and can be gone back to. */
  canRevert: boolean;
}


export interface Entry {
  id: string;
  amount: number;
  kind: string;
  date: string;
  voided: boolean;
  people: string[];
  /** Per-person share in integer cents, keyed by person id. Split server-side. */
  shares: Record<string, number>;
  /** Decrypted here. */
  reason: string;
  /** Decrypted here. Empty unless the entry was voided with a note. */
  voidReason: string;
  /** What it said before each correction, oldest first. Journal only. */
  corrections?: Correction[];
  /** What automatic splits set aside from it: person id -> goal id -> amount. */
  autoSaved?: Record<string, Record<string, number>>;
}

/** An entry as it read before a parent corrected it. */
export interface Correction {
  amount: number;
  reason: string;
  date: string;
  people: string[];
  /** When the correction was made. */
  at: string;
}

export const GOAL_KINDS = ['small', 'big'] as const;
export const GOAL_ICONS = ['icecream', 'trip', 'gift', 'bike', 'star', 'game'] as const;

export type GoalKind = (typeof GOAL_KINDS)[number];
export type GoalIcon = (typeof GOAL_ICONS)[number];

export interface Goal {
  id: string;
  /** Decrypted here. Goal titles are free text and carry names. */
  title: string;
  target: number;
  kind: GoalKind;
  icon: GoalIcon;
  due: string | null;
  /** `missed` is a parent saying it did not happen; see family/goals.ts. */
  status: 'active' | 'completed' | 'missed' | 'archived';
  /** The day it was completed or missed. Null while active. */
  closed: string | null;
  people: string[];
  caps: Record<string, number>;
  /**
   * What is IN the goal: the money set aside in it (or, once completed, what
   * completing it spent). Integer cents.
   */
  pot: number;
  /** Who put what in, in cents. `family` is the family pot. */
  funds: Record<string, number>;
  /** What completing it spent. Null until then. */
  spent: number | null;
  /**
   * How long a completed goal stays up on the valley and the children's
   * pages: 'always', a date, or null (not shown). See `isShowing`.
   */
  showUntil: string | null;
  /** Locked (the default): children can put money in but not take it out. */
  locked: boolean;
}

/** Whether a completed goal is still up, on this device's calendar. */
export function isShowing(goal: Pick<Goal, 'status' | 'showUntil'>, today: string): boolean {
  if (goal.status !== 'completed' || !goal.showUntil) return false;
  return goal.showUntil === 'always' || goal.showUntil >= today;
}

/** The family pot, wherever a person id would otherwise go. */
export const FAMILY_POT = 'family';

export type Every = 'week' | 'fortnight' | 'month';

/** One allowance: a child's, or the family pot's (the old monthly deposit). */
export interface AllowancePlan {
  amount: number;
  every: Every;
  /** The first payday, YYYY-MM-DD. */
  start: string;
  /** The next payday not yet paid. */
  next: string;
  paused: boolean;
}

/**
 * A label for an entry nobody typed a reason for: the ones the app makes
 * itself. An allowance payment is written by the server, which cannot seal a
 * reason, so it is named here.
 */
export function entryLabel(kind: string, reason: string): string {
  if (reason) return reason;
  if (kind === 'allowance') return 'Allowance';
  if (kind === 'monthly') return 'Monthly deposit';
  return '';
}

export interface QuickAction {
  id: string;
  /** Decrypted here. */
  reason: string;
  amount: number;
  people: string[];
}

export interface FamilyState {
  familyName: string;
  balance: number;
  month: string;
  today: string;
  earned: number;
  spent: number;
  carried: number;
  monthly: number;
  /** Money NOT in any goal, per person id and `family` for the family pot. */
  free: Record<string, number>;
  /** Allowance plans, by person id or `family`. */
  allowance: Record<string, AllowancePlan>;
  /** What the family counts in. Also set as the app's current unit. */
  unit: Unit;
  /** Automatic splits: per person id, a percentage of each deposit per goal id. */
  splits: Record<string, Record<string, number>>;
  /**
   * Who holds a copy of the family's name they can open on their own device
   * (`fam` and person ids). Home seals it again for anybody missing.
   */
  kidNameFor: string[];
  /**
   * Who holds a wrapping of a custom unit's name: `fam` and person ids. A
   * child missing from it cannot read the unit's name on their own device;
   * Home re-seals it for everybody when that happens (see `saveUnit`).
   */
  unitWrappedFor: string[];
  /** Where the painted valley reaches full health. Integer cents. */
  gardenTarget: number;
  /** The valley background the family chose (content/valley.toml [[scene]]); null: the default. */
  scene?: string | null;
  initialized: boolean;
  people: Person[];
  entries: Entry[];
  goals: Goal[];
  quickActions: QuickAction[];
  /**
   * How many devices are waiting for a parent to say yes. A count, not a
   * list: Home shows a banner from it without a second request, and the
   * detail belongs on the screen that can act on it.
   */
  pendingDevices: number;
  /** The family's own parents' locked devices asking to be let in. */
  pendingKeyRequests: number;
  /**
   * Whether the service requires verifiable parental consent before a child
   * uses it, and each child's consent and pause state (family/consent.ts).
   */
  consentRequired: boolean;
  privacy: Record<string, ConsentStatus>;
  revision: number;
}

/** A unit as the server holds it; a custom unit's name is ciphertext. */
export interface RawUnit {
  type: 'currency' | 'marbles' | 'custom';
  currency?: string;
  whole?: boolean;
  nameCt?: string | null;
  nameKeys?: Record<string, string>;
  /** A child's own wrapping, on the child route. */
  ukey?: string | null;
}

/**
 * Open a unit. A custom unit's name is `{one, many, icon}` sealed under a
 * content key; `opener` and `wrapped` are whichever key this device holds and
 * its wrapping of that content key. Unreadable becomes "points".
 */
export async function openUnit(
  raw: RawUnit | undefined,
  opener: CryptoKey | null,
  wrapped: string | null | undefined,
): Promise<Unit> {
  if (!raw) return DEFAULT_UNIT;
  if (raw.type === 'currency') return { type: 'currency', currency: raw.currency || 'USD' };
  if (raw.type === 'marbles') return { type: 'marbles' };
  let named: { one?: string; many?: string; icon?: string } = {};
  try {
    named = JSON.parse((await openReason(opener, raw.nameCt, wrapped)) || '{}');
  } catch {
    named = {};
  }
  return {
    type: 'custom',
    one: named.one || 'point',
    many: named.many || named.one || 'points',
    icon: named.icon || '',
    whole: raw.whole !== false,
  };
}

interface RawPerson {
  id: string;
  slot: number;
  status: Person['status'];
  pf?: string | null;
  /** This person's key, sealed under the family key. */
  pfw?: string | null;
  /** This person's own code, sealed under the family key. */
  codeCt?: string | null;
  /** A character a child has proposed, sealed like `pf`. */
  pendCt?: string | null;
  /** The character before the last approval, kept for an undo. */
  prevCt?: string | null;
}

interface RawEntry {
  id: string;
  amount: number;
  kind: string;
  date: string;
  voided: boolean;
  people?: string[];
  shares?: Record<string, number>;
  reasonCt?: string | null;
  reasonKeys?: Record<string, string>;
  voidReasonCt?: string | null;
}

interface RawState {
  consentRequired?: boolean;
  privacy?: Record<string, ConsentStatus>;
  pendingDevices?: number;
  pendingKeyRequests?: number;
  balance: number;
  month: string;
  today: string;
  earned: number;
  spent: number;
  carried: number;
  held: Record<string, number>;
  shared: Record<string, number>;
  free?: Record<string, number>;
  allowance?: Record<string, AllowancePlan>;
  unit?: RawUnit;
  splits?: Record<string, Record<string, number>>;
  kidNameFor?: string[];
  settings: {
    nameCt?: string | null;
    /** The family code under the family key; parents only. */
    codeCt?: string | null;
    monthly: number;
    gardenTarget?: number;
    scene?: string | null;
    initialized: boolean;
  };
  people: RawPerson[];
  transactions: RawEntry[];
  goals: RawGoal[];
  quick_actions: RawQuick[];
  revision: number;
}

interface RawGoal {
  id: string;
  titleCt?: string | null;
  titleKeys?: Record<string, string>;
  target: number;
  kind: GoalKind;
  icon: GoalIcon;
  due?: string | null;
  status: Goal['status'];
  closed?: string | null;
  funds?: Record<string, number>;
  spent?: number | null;
  showUntil?: string | null;
  locked?: boolean;
  people?: string[];
  caps?: Record<string, number>;
  pot: number;
}

interface RawQuick {
  id: string;
  reasonCt?: string | null;
  amount: number;
  people?: string[];
}

/**
 * Open a ciphertext field, and survive one that will not open.
 *
 * A row written under a family key this device no longer has is not worth
 * throwing over: it would take down the whole screen for one unreadable field.
 * The person still exists and their money is still right; the name is simply
 * blank until a device that can read it opens the family.
 */
async function readText(key: CryptoKey, sealed?: string | null): Promise<string> {
  if (!sealed) return '';
  try {
    return await decryptField(key, sealed);
  } catch {
    return '';
  }
}

/**
 * A person's name and their own key.
 *
 * `pf` is sealed under the PERSON key so a child's device can read its own
 * name, and the person key is sealed under the family key as `pfw` so a parent
 * can always get at it. Somebody added before that existed has no `pfw` and a
 * fragment sealed directly under the family key -- handled here rather than by
 * a migration, because a migration would have to rewrite rows this device may
 * not be the right one to rewrite.
 */
async function readPerson(
  familyKey: CryptoKey,
  raw: RawPerson,
): Promise<{ name: string; avatar: AvatarSpec | null; key: CryptoKey | null;
            pending: Person['pending'] }> {
  let key: CryptoKey | null = null;
  if (raw.pfw) {
    try {
      key = await openPersonKey(familyKey, raw.pfw);
    } catch {
      key = null;
    }
  }
  const current = parseFragment(await readText(key ?? familyKey, raw.pf));
  let pending: Person['pending'] = null;
  if (raw.pendCt) {
    // A child's device seals its draft with the child's own key, so that is
    // what opens it. No key means nothing to show, not an error.
    const draft = key ? parseFragment(await readText(key, raw.pendCt)) : null;
    if (draft) {
      pending = {
        avatar: draft.avatar ?? null,
        name: draft.name,
        renamed: Boolean(draft.name) && draft.name !== (current?.name ?? ''),
      };
    }
  }
  return { name: current?.name ?? '', avatar: current?.avatar ?? null, key, pending };
}

/**
 * Just the revision number. One GetItem on one small item.
 *
 * This is the route the whole polling design rests on: a client that has not
 * seen the number move does not re-read the family, which takes a display
 * from about $6.75 a month in reads to about seven cents. Nothing on the
 * parent side needed it until Home had a reason to watch for a change it did
 * not cause -- a child waiting to be approved.
 */
export async function readRevision(familyId: string): Promise<number> {
  const result = await api.get<{ revision?: number }>(
    `/v1/families/${familyId}/revision`,
  );
  return result.revision ?? 0;
}

/**
 * This device's family key no longer fits the family: it started over with a
 * new one (services/api/valley/startover.py). Thrown rather than carried on,
 * because every decrypt here fails QUIETLY to a blank -- a device holding the
 * old key would show a family with no names, and anything it then wrote
 * would be sealed under a key nobody else has.
 */
export class WrongFamilyKey extends Error {
  constructor() {
    super('Your family started over with a new key. Enter the new family code on this device.');
    this.name = 'WrongFamilyKey';
  }
}

export async function loadState(familyId: string, key: CryptoKey): Promise<FamilyState> {
  // The device's UTC offset in minutes, so the server's "today" is the
  // family's: an entry made at nine in the evening is dated today, and an
  // allowance due Friday is paid on Friday.
  const raw = await api.get<RawState>(`/v1/families/${familyId}/state`, {
    tz: String(-new Date().getTimezoneOffset()),
  });

  // The family code is sealed under the family key, so opening it is a
  // one-decrypt check that this is still the right key.
  if (raw.settings.codeCt) {
    try {
      await decryptField(key, raw.settings.codeCt);
    } catch {
      await forgetKey(familyId);
      throw new WrongFamilyKey();
    }
  }

  const unit = await openUnit(raw.unit, key, raw.unit?.nameKeys?.[FAMILY]);
  setUnit(unit);
  setFamilyScene(raw.settings.scene);

  const [familyName, people, entries, goals, quickActions] = await Promise.all([
    readText(key, raw.settings.nameCt),
    Promise.all(
      raw.people.map(async (person) => {
        const { name, avatar, key: personKey, pending } = await readPerson(key, person);
        return {
          id: person.id,
          slot: person.slot,
          status: person.status,
          name,
          key: personKey,
          codeCt: person.codeCt ?? null,
          held: raw.held[person.id] ?? 0,
          shared: raw.shared[person.id] ?? 0,
          avatar,
          pending,
          canRevert: Boolean(person.prevCt),
        };
      }),
    ),
    Promise.all(
      raw.transactions.map(async (entry) => ({
        id: entry.id,
        amount: entry.amount,
        kind: entry.kind,
        date: entry.date,
        voided: entry.voided,
        people: entry.people ?? [],
        shares: entry.shares ?? {},
        reason: entryLabel(entry.kind, await openReason(key, entry.reasonCt, entry.reasonKeys?.[FAMILY])),
        voidReason: await readText(key, entry.voidReasonCt),
      })),
    ),
    Promise.all(
      (raw.goals ?? []).map(async (goal) => ({
        id: goal.id,
        // The family's own wrapping of the title key, or -- for a goal saved
        // before title keys existed -- nothing, in which case the title is
        // sealed directly under the family key. `openReason` handles both,
        // which is why it is the same call the reasons use.
        title: await openReason(key, goal.titleCt, goal.titleKeys?.[FAMILY]),
        target: goal.target,
        kind: goal.kind,
        icon: goal.icon,
        due: goal.due ?? null,
        status: goal.status,
        closed: goal.closed ?? null,
        people: goal.people ?? [],
        caps: goal.caps ?? {},
        pot: goal.pot,
        funds: goal.funds ?? {},
        spent: goal.spent ?? null,
        showUntil: goal.showUntil ?? null,
        locked: goal.locked ?? true,
      })),
    ),
    Promise.all(
      (raw.quick_actions ?? []).map(async (quick) => ({
        id: quick.id,
        reason: await readText(key, quick.reasonCt),
        amount: quick.amount,
        people: quick.people ?? [],
      })),
    ),
  ]);

  return {
    familyName,
    balance: raw.balance,
    month: raw.month,
    today: raw.today,
    earned: raw.earned,
    spent: raw.spent,
    carried: raw.carried,
    monthly: raw.settings.monthly,
    free: raw.free ?? {},
    allowance: raw.allowance ?? {},
    unit,
    unitWrappedFor: Object.keys(raw.unit?.nameKeys ?? {}),
    splits: raw.splits ?? {},
    kidNameFor: raw.kidNameFor ?? [],
    gardenTarget: raw.settings.gardenTarget ?? 10000,
    scene: raw.settings.scene ?? null,
    initialized: raw.settings.initialized,
    people: people.sort((a, b) => a.slot - b.slot),
    entries,
    goals,
    quickActions,
    pendingDevices: raw.pendingDevices ?? 0,
    pendingKeyRequests: raw.pendingKeyRequests ?? 0,
    consentRequired: raw.consentRequired ?? false,
    privacy: raw.privacy ?? {},
    revision: raw.revision,
  };
}

/**
 * First run. Nothing else in the API works until this lands -- every other
 * mutation answers "set up your family garden first" -- so it is necessarily
 * the screen that follows creating a family.
 */
export async function runSetup(
  familyId: string,
  key: CryptoKey,
  values: {
    name: string;
    monthly: string;
    seed: string;
    /**
     * The same name sealed under the family CODE, if the caller has it.
     *
     * Two seals of one string, because two different people have to read it:
     * `nameCt` for members holding the family key, and this for somebody
     * holding only a join link. Passed in already sealed rather than built
     * here, so this module never needs to know what a family code is.
     */
    preview?: string;
  },
): Promise<void> {
  const [nameCt, monthlyReasonCt, seedReasonCt] = await Promise.all([
    encryptField(key, values.name),
    encryptField(key, 'Monthly deposit'),
    encryptField(key, 'Starting money'),
  ]);
  await api.write('POST', `/v1/families/${familyId}/setup`, {
    nameCt,
    seed: values.seed,
    monthlyReasonCt,
    seedReasonCt,
    ...(values.preview ? { preview: values.preview } : {}),
    credit_now: false,
  });
}

/**
 * The valley background, for everybody in the family at once.
 *
 * Plain on the server: which of the app's own paintings to show says nothing
 * about anybody. null goes back to the default.
 */
export async function saveScene(familyId: string, scene: string | null): Promise<void> {
  await api.write('PUT', `/v1/families/${familyId}/settings`, { scene });
  setFamilyScene(scene);
}

/**
 * The family's name and its monthly deposit.
 *
 * Both used to live on the setup screen, which ran once and then could not be
 * reached again -- so the name a parent typed in their first thirty seconds
 * was the name forever. They are ordinary settings and they belong in
 * Settings.
 *
 * `preview` is the same name sealed under the family CODE. It has to go up
 * with the rename, or a join link keeps announcing the old name to the one
 * person who cannot check: somebody holding the code who has not joined.
 */
export async function saveSettings(
  familyId: string,
  key: CryptoKey,
  values: { name: string; preview?: string },
  /**
   * The roster, so the name is also sealed for each child -- their device
   * shows which family it is on, and a child can belong to two (two
   * households, grandparents). Omitted, the children's copy is left as it is.
   */
  roster?: Person[],
): Promise<void> {
  let kids = {};
  if (roster) {
    const personKeys = new Map<string, CryptoKey>();
    for (const person of roster) if (person.key && person.status !== 'removed') personKeys.set(person.id, person.key);
    const sealed = await sealReason(key, personKeys, values.name);
    kids = { kidNameCt: sealed.reasonCt, kidNameKeys: sealed.reasonKeys };
  }
  await api.write('PUT', `/v1/families/${familyId}/settings`, {
    nameCt: await encryptField(key, values.name),
    ...(values.preview ? { preview: values.preview } : {}),
    ...kids,
  });
}


/**
 * Add or rename somebody.
 *
 * The id is generated here, not by the server, so a retry cannot produce two
 * people with the same name -- the same reasoning as every other write in this
 * app. It is opaque and means nothing.
 */
export async function savePerson(
  familyId: string,
  key: CryptoKey,
  fragment: PersonFragment | string,
  person?: { id: string; key: CryptoKey | null },
): Promise<void> {
  // THE WHOLE FRAGMENT, never just the name. This used to write
  // `{ name }` and nothing else, which was harmless while a name was all a
  // fragment held -- and would have silently erased a child's character the
  // first time anybody renamed them. A bare string is still accepted, for
  // the one caller that genuinely has only a name: adding somebody new.
  const sealed: PersonFragment = typeof fragment === 'string' ? { name: fragment } : fragment;
  const id = person?.id ?? crypto.randomUUID().replace(/-/g, '');

  // A person's name is sealed under their OWN key, not the family key, so a
  // child's device can read its own name without holding the family's. The
  // person key is then sealed under the family key as `pfw`, which is how a
  // parent gets it back on any device.
  //
  // An existing person keeps the key they already have. Minting a new one
  // would orphan every `rkey` already written against the old one, and those
  // rows are the child's own journal.
  //
  // A person added BEFORE per-person keys existed has `key: null` and falls
  // into the same branch as a brand-new one, so they get a key here. That is
  // the whole migration: Settings saves the name once when a parent opens
  // that child's code section, and from then on the child's device can be
  // approved and can read its own name. Nothing else has to run.
  const personKey = person?.key ?? (await createPersonKey());

  await api.write('PUT', `/v1/families/${familyId}/people/${id}`, {
    pf: await encryptField(personKey, sealable(sealed)),
    pfw: await sealPersonKey(key, personKey),
    pfv: 1,
  });
}

/**
 * A parent setting somebody's character directly. Takes effect at once --
 * a parent is who approves characters, so there is nobody to ask.
 *
 * The name goes back in unchanged: this is the same sealed fragment, and the
 * name is half of it.
 */
export async function saveLook(
  familyId: string,
  key: CryptoKey,
  person: Pick<Person, 'id' | 'key' | 'name'>,
  avatar: AvatarSpec,
  name: string = person.name,
): Promise<void> {
  await savePerson(familyId, key, { name, avatar }, person);
}

/**
 * A look designed on the full valley, which runs on a parent's session but
 * is left up where children use it. So it does what a child's own page does:
 * queue the look for a grown-up, leaving the approved one on screen.
 *
 * Sealed under the PERSON's key, as a child's device seals it, so the parent
 * side reads it with the key it already uses for `pendCt`. A person added
 * before per-person keys has none; saving them once as they are (which mints
 * one, see `savePerson`) comes first.
 */
export async function draftLook(
  familyId: string,
  key: CryptoKey,
  person: Pick<Person, 'id' | 'key' | 'name' | 'avatar'>,
  avatar: AvatarSpec,
  name: string = person.name,
): Promise<void> {
  let personKey = person.key;
  if (!personKey) {
    personKey = await createPersonKey();
    await api.write('PUT', `/v1/families/${familyId}/people/${person.id}`, {
      pf: await encryptField(personKey, sealable({ name: person.name, ...(person.avatar ? { avatar: person.avatar } : {}) })),
      pfw: await sealPersonKey(key, personKey),
      pfv: 1,
    });
  }
  await api.write('POST', `/v1/families/${familyId}/people/${person.id}/draft`, {
    pf: await encryptField(personKey, sealable({ name, avatar })),
    pfv: 1,
  });
}

/**
 * Publish what a child designed. The server moves their draft into place and
 * keeps the character it replaces, so a regretted approval is one tap back.
 */
export async function approveLook(familyId: string, personId: string): Promise<void> {
  await api.write('POST', `/v1/families/${familyId}/people/${personId}/approve`, {});
}

/** Say no to a child's draft. Their current character is untouched. */
export async function discardLook(familyId: string, personId: string): Promise<void> {
  await api.write('POST', `/v1/families/${familyId}/people/${personId}/discard`, {});
}

/** Go back to the character before the last approval. */
export async function revertLook(familyId: string, personId: string): Promise<void> {
  await api.write('POST', `/v1/families/${familyId}/people/${personId}/revert`, {});
}

export async function removePerson(familyId: string, personId: string): Promise<void> {
  await api.write('DELETE', `/v1/families/${familyId}/people/${personId}`);
}

/**
 * Record money.
 *
 * `amount` is the typed string, already signed by intent.
 *
 * The reason is sealed under a fresh content key, and that key is wrapped for
 * the family and for each person named on the row -- so the child whose row it
 * is can read why their money changed, on a device that does not hold the
 * family key. `roster` is needed for the person keys; anybody in it without one
 * simply gets no wrapping, and their row reads the way it always did.
 */
export async function recordMoney(
  familyId: string,
  key: CryptoKey,
  entry: { amount: string; reason: string; people: string[]; date?: string; id?: string },
  roster: Person[] = [],
): Promise<void> {
  const personKeys = new Map<string, CryptoKey>();
  for (const person of roster) {
    if (entry.people.includes(person.id) && person.key) personKeys.set(person.id, person.key);
  }
  const sealed = await sealReason(key, personKeys, entry.reason);

  await api.write('POST', `/v1/families/${familyId}/transactions`, {
    amount: entry.amount,
    reasonCt: sealed.reasonCt,
    reasonKeys: sealed.reasonKeys,
    people: entry.people,
    ...(entry.date ? { date: entry.date } : {}),
    // Given by a restore, so running it twice writes each entry once.
    ...(entry.id ? { id: entry.id } : {}),
  });
}

/**
 * Save a goal, new or edited.
 *
 * The title is encrypted: "Ice cream for Ravi's birthday" is exactly the kind
 * of sentence this whole design exists to keep off the server.
 *
 * `caps` limits how much of a named person's money counts toward the goal, so
 * a child saving for something of their own is not quietly funding a family
 * trip. Cents, keyed by person id, sent as strings for the same reason amounts
 * always are.
 */
export async function saveGoal(
  familyId: string,
  key: CryptoKey,
  goal: {
    id?: string;
    title: string;
    target: string;
    kind: GoalKind;
    icon: GoalIcon;
    due?: string;
    people: string[];
    caps?: Record<string, string>;
  },
  /**
   * The roster, so each person named on the goal gets their own wrapping of
   * its title key. Without this a child sees "A surprise" where the name of
   * the thing they are saving for should be -- the title was sealed under the
   * family key, which their device deliberately does not hold.
   *
   * Optional, so a caller that has no roster to hand still saves a working
   * goal; it just will not be readable by a child until it is next saved
   * from a screen that does.
   */
  roster: Person[] = [],
): Promise<void> {
  const id = goal.id ?? crypto.randomUUID().replace(/-/g, '');
  // A goal naming nobody is everybody's, so every child gets a wrapping and
  // can read what the family is saving for on their own device.
  const named = new Map(
    roster
      .filter((person) => (goal.people.length === 0 || goal.people.includes(person.id)) && person.key)
      .map((person) => [person.id, person.key!] as const),
  );
  // Exactly the shape a transaction reason uses, and the same call: a fresh
  // key for this title, wrapped once for the family and once for each person
  // on it. See crypto/content.ts.
  const sealed = await sealReason(key, named, goal.title);

  await api.write('PUT', `/v1/families/${familyId}/goals/${id}`, {
    titleCt: sealed.reasonCt,
    titleKeys: sealed.reasonKeys,
    target: goal.target,
    kind: goal.kind,
    icon: goal.icon,
    people: goal.people,
    ...(goal.due ? { due: goal.due } : {}),
    ...(goal.caps ? { caps: goal.caps } : {}),
  });
}

/**
 * Spend a goal.
 *
 * The server refuses unless the pot actually covers the target, and it writes
 * the spend and completes the goal under a version check -- so two parents
 * redeeming the same goal at the same moment cannot both succeed.
 */
export async function redeemGoal(
  familyId: string,
  key: CryptoKey,
  goalId: string,
  reason?: string,
  /** How long it stays up afterwards: 'always', a date, or not at all. */
  showUntil?: string | null,
): Promise<void> {
  await api.write('POST', `/v1/families/${familyId}/goals/${goalId}/redeem`, {
    ...(reason ? { reasonCt: await encryptField(key, reason) } : {}),
    ...(showUntil ? { showUntil } : {}),
  });
}

/** Lock a goal (children put in, never take out) or unlock it. */
export async function lockGoal(familyId: string, goalId: string, locked: boolean): Promise<void> {
  await api.write('POST', `/v1/families/${familyId}/goals/${goalId}/lock`, { locked });
}

/** Change how long a completed goal stays up; null takes it down now. */
export async function showGoal(familyId: string, goalId: string, showUntil: string | null): Promise<void> {
  await api.write('POST', `/v1/families/${familyId}/goals/${goalId}/show`, { showUntil });
}

/**
 * The family's automatic splits, all at once: per child, a whole percentage
 * of every deposit into each of some goals (at most 100 per cent).
 */
export async function saveSplits(
  familyId: string,
  splits: Record<string, Record<string, number>>,
): Promise<void> {
  await api.write('PUT', `/v1/families/${familyId}/splits`, { splits });
}

export async function archiveGoal(familyId: string, goalId: string): Promise<void> {
  await api.write('POST', `/v1/families/${familyId}/goals/${goalId}/archive`, {});
}

/**
 * Set how much each person -- and the family pot -- has in a goal. Absolute
 * amounts, as the dollar strings the API parses, so a retry cannot double one.
 *
 * The server refuses more than somebody has free. The Goals screen checks
 * first and offers what IS free; see `fundsProblem`.
 */
export async function setGoalFunds(
  familyId: string,
  goalId: string,
  funds: Record<string, string>,
  /** A parent taking a child below zero, knowingly. */
  force = false,
): Promise<void> {
  await api.write('POST', `/v1/families/${familyId}/goals/${goalId}/funds`, {
    funds,
    ...(force ? { force: true } : {}),
  });
}

/** Give everything in a goal back to whoever put it in. The goal stays. */
export async function refundGoal(familyId: string, goalId: string): Promise<void> {
  await api.write('POST', `/v1/families/${familyId}/goals/${goalId}/refund`, {});
}

/**
 * Change what the family counts in.
 *
 * A custom unit's name -- `{one, many, icon}` -- is sealed exactly like a
 * transaction reason: a fresh key, wrapped for the family and for each person
 * who has a key, so every child's own device can read what they count in.
 * Nothing already recorded is changed (services/api/valley/units.py).
 */
export async function saveUnit(
  familyId: string,
  key: CryptoKey,
  unit: Unit,
  roster: Person[] = [],
): Promise<void> {
  if (unit.type !== 'custom') {
    await api.write('PUT', `/v1/families/${familyId}/unit`, unit);
    return;
  }
  const personKeys = new Map<string, CryptoKey>();
  for (const person of roster) if (person.key && person.status !== 'removed') personKeys.set(person.id, person.key);
  const sealed = await sealReason(
    key,
    personKeys,
    JSON.stringify({ one: unit.one.trim(), many: unit.many.trim(), icon: unit.icon.trim() }),
  );
  await api.write('PUT', `/v1/families/${familyId}/unit`, {
    type: 'custom',
    whole: unit.whole,
    nameCt: sealed.reasonCt,
    nameKeys: sealed.reasonKeys,
  });
}

/**
 * The family's allowance plans, all at once. A plan left out is cancelled;
 * "pause everybody" is every plan sent with `paused`.
 */
export async function saveAllowance(
  familyId: string,
  plans: Record<string, { amount: string; every: Every; start: string; paused: boolean }>,
): Promise<void> {
  await api.write('PUT', `/v1/families/${familyId}/allowance`, { plans });
}

/**
 * A parent saying a goal did not happen. Never done for them, even long past
 * its date -- see family/goals.ts. No money moves.
 */
export async function missGoal(familyId: string, goalId: string): Promise<void> {
  await api.write('POST', `/v1/families/${familyId}/goals/${goalId}/miss`, {});
}

/** Try a missed goal again, with a new date or none. */
export async function reopenGoal(familyId: string, goalId: string, due: string | null): Promise<void> {
  await api.write('POST', `/v1/families/${familyId}/goals/${goalId}/reopen`, { due });
}

/**
 * Give an active goal a new date (or none). A save of the whole goal, as the
 * server stores goals whole: everything else goes back exactly as it was,
 * caps turned back from cents into the dollar strings the API takes.
 */
export async function moveGoalDate(
  familyId: string,
  key: CryptoKey,
  goal: Goal,
  due: string | null,
  roster: Person[],
): Promise<void> {
  await saveGoal(
    familyId,
    key,
    {
      id: goal.id,
      title: goal.title,
      target: (goal.target / 100).toFixed(2),
      kind: goal.kind,
      icon: goal.icon,
      ...(due ? { due } : {}),
      people: goal.people,
      caps: Object.fromEntries(Object.entries(goal.caps).map(([id, cents]) => [id, (cents / 100).toFixed(2)])),
    },
    roster,
  );
}

/** A button a parent presses often: one amount, one reason, the same people. */
export async function saveQuickAction(
  familyId: string,
  key: CryptoKey,
  quick: { id?: string; reason: string; amount: string; people: string[] },
): Promise<void> {
  const id = quick.id ?? crypto.randomUUID().replace(/-/g, '');
  await api.write('PUT', `/v1/families/${familyId}/quick-actions/${id}`, {
    reasonCt: await encryptField(key, quick.reason),
    amount: quick.amount,
    people: quick.people,
  });
}

export async function removeQuickAction(familyId: string, quickId: string): Promise<void> {
  await api.write('DELETE', `/v1/families/${familyId}/quick-actions/${quickId}`);
}
